import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on page change
  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesDropdownOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Forside', path: '/' },
    {
      name: 'Ydelser',
      path: '/ydelser',
      hasDropdown: true,
      subItems: [
        { name: 'Content Opsætning & Redigering', path: '/ydelser/content-redigering' },
        { name: 'Online Vejledning (Foto & Video)', path: '/ydelser/online-vejledning' },
        { name: 'Meta SoMe Postkalender', path: '/ydelser/meta-postkalender' },
        { name: 'Opsætning af Sociale Medier', path: '/ydelser/profil-opsaetning' },
      ],
    },
    { name: 'Sådan arbejder vi', path: '/saadan-arbejder-vi' },
    { name: 'Cases & Erfaring', path: '/cases' },
    { name: 'Om os', path: '/om-os' },
    { name: 'Kontakt', path: '/kontakt' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-sm'
          : 'bg-white shadow-2xs'
      }`}
    >
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300 ${
        isScrolled ? 'py-2 sm:py-2.5' : 'py-3 sm:py-3.5'
      }`}>
        {/* Top Tier: Logo & Primary CTA */}
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 text-[#161514] font-bold tracking-tight transition-opacity hover:opacity-90 group py-1"
          >
            <div className="flex flex-col justify-center select-none text-left">
              <span className="font-black text-xl sm:text-2xl text-[#161514] tracking-tight leading-none">
                My Service
              </span>
              <span className="text-[11px] sm:text-xs font-bold text-[#A8885B] tracking-[0.22em] uppercase leading-none mt-1">
                For You
              </span>
            </div>
          </Link>

          {/* Desktop Right Actions (Top Bar) */}
          <div className="hidden xl:flex items-center gap-5">
            <div className="flex items-center gap-4 text-xs font-medium text-[#7B756D]">
              <a href="tel:+4561489206" className="flex items-center gap-1.5 hover:text-[#161514] transition-colors">
                <i className="fa-light fa-phone text-[#9C9489]" aria-hidden="true" />
                <span className="whitespace-nowrap font-medium text-[#4A4640]">+45 61 48 92 06</span>
              </a>
              <span className="text-gray-300">|</span>
              <a href="mailto:info.myserviceforyou@gmail.com" className="flex items-center gap-1.5 hover:text-[#161514] transition-colors">
                <i className="fa-light fa-envelope text-[#9C9489]" aria-hidden="true" />
                <span className="whitespace-nowrap font-medium text-[#4A4640]">info.myserviceforyou@gmail.com</span>
              </a>
            </div>

            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-full bg-[#161514] text-[#FAFAF8] text-xs sm:text-sm font-semibold hover:bg-[#2A2926] active:scale-98 transition-all shadow-xs"
            >
              <span>Kontakt os</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Mobile & Tablet Trigger - visible below 1200px */}
          <div className="flex xl:hidden items-center gap-2.5">
            <Link
              to="/kontakt"
              className="px-3.5 py-2 rounded-full bg-[#161514] text-[#FAFAF8] text-xs sm:text-sm font-semibold hover:bg-[#2A2926] transition-colors"
            >
              Kontakt
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-[#161514] flex items-center justify-center text-2xl hover:text-black transition-colors cursor-pointer"
              aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              aria-expanded={mobileMenuOpen}
            >
              <i className={mobileMenuOpen ? 'fa-regular fa-xmark' : 'fa-regular fa-bars'} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Bottom Tier: Menu Links Under Logo - visible ONLY at 1200px+ (xl breakpoint) */}
        <div className="hidden xl:flex items-center justify-between pt-2.5 mt-2 border-t border-gray-100/90">
          <nav className="flex items-center gap-1 2xl:gap-2">
            {navLinks.map((item) => {
              if (item.hasDropdown) {
                const isServicesActive = location.pathname.startsWith('/ydelser');
                return (
                  <div
                    key={item.name}
                    className="relative group z-30"
                    onMouseEnter={() => setServicesDropdownOpen(true)}
                    onMouseLeave={() => setServicesDropdownOpen(false)}
                  >
                    <button
                      type="button"
                      onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs lg:text-sm font-medium transition-colors cursor-pointer ${
                        isServicesActive
                          ? 'text-[#161514] font-semibold'
                          : 'text-[#6B655C] hover:text-[#161514]'
                      }`}
                      aria-expanded={servicesDropdownOpen}
                      aria-haspopup="true"
                    >
                      <span>{item.name}</span>
                      <i
                        className={`fa-solid fa-chevron-down text-[10px] transition-transform duration-200 ${
                          servicesDropdownOpen ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>

                    {/* Dropdown Menu */}
                    <div
                      className={`absolute top-full left-0 pt-2 w-64 z-50 transition-all duration-200 ${
                        servicesDropdownOpen
                          ? 'opacity-100 translate-y-0 pointer-events-auto'
                          : 'opacity-0 translate-y-1 pointer-events-none'
                      }`}
                    >
                      <div className="bg-white border border-gray-100 rounded-2xl p-2 shadow-2xl ring-1 ring-black/5">
                        {item.subItems.map((sub) => (
                          <Link
                            key={sub.name}
                            to={sub.path}
                            className="block px-3.5 py-2 text-xs font-medium text-[#4A4640] hover:text-[#161514] hover:bg-gray-50 rounded-xl transition-colors"
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `px-3 py-1.5 text-xs lg:text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-[#161514] font-semibold'
                        : 'text-[#6B655C] hover:text-[#161514]'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Kampagne Banner Under Nav */}
      <Link
        to="/kontakt"
        className="block relative z-10 w-full overflow-hidden bg-[#161514] text-[#D8D2C9] border-b border-[#282623] py-1.5 sm:py-2 group hover:text-white transition-colors cursor-pointer"
        title="Se tilbud og kontakt os"
      >
        {/* Gradient fade i venstre og højre side for diskret overgang */}
        <div className="absolute left-0 inset-y-0 w-8 sm:w-16 bg-gradient-to-r from-[#161514] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 inset-y-0 w-8 sm:w-16 bg-gradient-to-l from-[#161514] to-transparent z-10 pointer-events-none" />

        <div className="animate-banner-ticker flex items-center">
          {[0, 1, 2, 3].map((idx) => (
            <div
              key={idx}
              className="inline-flex items-center gap-2 sm:gap-2.5 px-6 sm:px-8 whitespace-nowrap text-xs font-normal tracking-wide"
            >
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-white/5 border border-white/10 text-[#C5A880] text-[10px] font-medium tracking-wider uppercase">
                Tilbud
              </span>
              <span className="text-[#FAF8F5]/90 font-medium">
                Betal for 2 måneder og få 1 måned gratis
              </span>
              <span className="text-[#6B655C]">•</span>
              <span className="text-[#A8A29A]">
                0 binding på alle aftaler
              </span>
              <span className="inline-flex items-center gap-1 text-[#C5A880] group-hover:text-white transition-colors text-[11px] font-medium ml-1">
                <span>Kontakt os</span>
                <i className="fa-regular fa-arrow-right text-[9px] group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
              </span>
              <span className="text-[#423E39] ml-4 select-none text-[10px]" aria-hidden="true">/</span>
            </div>
          ))}
        </div>
      </Link>

      {/* Mobile Drawer (Under 1200px) */}
      {mobileMenuOpen && (
        <div className="xl:hidden fixed inset-x-0 top-[102px] sm:top-[106px] bottom-0 bg-white z-50 overflow-y-auto border-t border-gray-100 p-6 flex flex-col justify-between">
          <div className="space-y-1.5">
            {navLinks.map((item) => {
              if (item.hasDropdown) {
                return (
                  <div key={item.name} className="py-1">
                    <button
                      type="button"
                      onClick={() => setServicesDropdownOpen(!servicesDropdownOpen)}
                      className="w-full flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-gray-50 text-left cursor-pointer"
                    >
                      <span className="text-base font-semibold text-[#161514]">
                        {item.name}
                      </span>
                      <span className="p-2 text-[#6B655C]">
                        <i
                          className={`fa-solid fa-chevron-down text-xs transition-transform ${
                            servicesDropdownOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </span>
                    </button>
                    {servicesDropdownOpen && (
                      <div className="ml-4 pl-3 border-l-2 border-gray-200 space-y-1 my-1">
                        {item.subItems.map((sub) => (
                          <Link
                            key={sub.name}
                            to={sub.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block py-2 text-sm text-[#5C5852] hover:text-[#161514]"
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  end={item.path === '/'}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-3 text-base font-medium transition-colors ${
                      isActive
                        ? 'text-[#161514] font-bold'
                        : 'text-[#5C5852] hover:text-[#161514]'
                    }`
                  }
                >
                  {item.name}
                </NavLink>
              );
            })}
          </div>

          <div className="pt-8 border-t border-gray-100 space-y-4">
            <Link
              to="/kontakt"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-full bg-[#161514] text-[#FAFAF8] font-semibold text-center hover:bg-[#2A2926] shadow-sm transition-colors"
            >
              <span>Kontakt os for en aftale</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
            <div className="text-xs text-[#7B756D] text-center space-y-1">
              <p>Telefon: <a href="tel:+4561489206" className="no-break font-medium text-[#161514] hover:underline">+45 61 48 92 06</a></p>
              <p>Email: <a href="mailto:info.myserviceforyou@gmail.com" className="no-break font-medium text-[#161514] hover:underline">info.myserviceforyou@gmail.com</a></p>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
